import { useCallback, useEffect, useRef } from "react";
import api from "../api/axiosConfig";
import { extractApiData } from "../api/response";
import { emitGlobalFeedback } from "./GlobalFeedback";

const CHECK_DELAY_MS = 1200;
const MAX_ACHIEVEMENT_NOTICES = 3;

function getUserKey(profile) {
  return String(profile?._id || profile?.id || "").trim();
}

function normalizeAchievementsSnapshot(payload) {
  const data = extractApiData(payload) || {};
  const items = Array.isArray(data.items)
    ? data.items
    : Array.isArray(data.achievements)
      ? data.achievements
      : [];
  const profile = data.profile || data.stats || {};
  return { items, profile };
}

function normalizeXpSnapshot(payload) {
  const data = extractApiData(payload) || {};
  return data.profile || data.stats || {};
}

function readStoredJson(key, fallback) {
  try {
    const parsed = JSON.parse(localStorage.getItem(key) || "");
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
}

function writeStoredJson(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage lleno o bloqueado: el aviso no debe romper la app.
  }
}

function readStoredString(key) {
  try {
    return localStorage.getItem(key) || "";
  } catch {
    return "";
  }
}

function writeStoredString(key, value) {
  try {
    localStorage.setItem(key, String(value));
  } catch {
    // Storage lleno o bloqueado: el aviso no debe romper la app.
  }
}

function getAchievementTitle(item) {
  return String(item?.title || item?.name || "Logro").trim();
}

function getAchievementKey(item) {
  return String(item?.key || item?.achievementKey || "").trim();
}

function getUnlockedAchievementKeys(items) {
  return items
    .filter((item) => Boolean(item?.unlocked) || String(item?.status || "").toLowerCase() === "unlocked")
    .map((item) => getAchievementKey(item))
    .filter(Boolean)
    .sort();
}

export default function XpEventNotifier({ profile }) {
  const userKey = getUserKey(profile);
  const timerRef = useRef(null);
  const inFlightRef = useRef(false);

  const runCheck = useCallback(
    async (reason = "manual") => {
      if (!userKey || inFlightRef.current) return;
      inFlightRef.current = true;
      try {
        const [achievementsResult, xpResult] = await Promise.allSettled([
          api.get("/achievements/me", { timeout: 12000, silentGlobalError: true }),
          api.get("/xp/me/history?limit=1", { timeout: 12000, silentGlobalError: true }),
        ]);

        if (achievementsResult.status === "fulfilled") {
          const snapshot = normalizeAchievementsSnapshot(achievementsResult.value?.data);
          const unlockedKeys = getUnlockedAchievementKeys(snapshot.items);
          const knownKey = `xphub:known-achievements:v2:${userKey}`;
          const previousKeys = readStoredJson(knownKey, null);
          writeStoredJson(knownKey, unlockedKeys);

          if (Array.isArray(previousKeys)) {
            const previousSet = new Set(previousKeys.map(String));
            const newKeys = unlockedKeys.filter((key) => !previousSet.has(key));
            const byKey = new Map(snapshot.items.map((item) => [getAchievementKey(item), item]));

            newKeys.slice(0, MAX_ACHIEVEMENT_NOTICES).forEach((key) => {
              const achievement = byKey.get(key);
              emitGlobalFeedback({
                type: "achievement",
                title: "Logro desbloqueado",
                message: getAchievementTitle(achievement),
                actionLabel: "Ver logro",
                actionTo: `/user-profile?tab=achievements&achievement=${encodeURIComponent(key)}`,
                durationMs: 7200,
              });
            });

            if (newKeys.length > MAX_ACHIEVEMENT_NOTICES) {
              emitGlobalFeedback({
                type: "achievement",
                title: `${newKeys.length} logros desbloqueados`,
                message: "Tu progreso acaba de actualizarse.",
                actionLabel: "Ver logros",
                actionTo: "/user-profile?tab=achievements",
                durationMs: 7600,
              });
            }
          }
        }

        if (xpResult.status === "fulfilled") {
          const xpProfile = normalizeXpSnapshot(xpResult.value?.data);
          const currentLevel = Math.max(1, Math.trunc(Number(xpProfile?.level || 1)));
          const levelKey = `xphub:last-level:v1:${userKey}`;
          const previousLevel = Number(readStoredString(levelKey));
          writeStoredString(levelKey, currentLevel);

          if (Number.isFinite(previousLevel) && previousLevel > 0 && currentLevel > previousLevel) {
            emitGlobalFeedback({
              type: "level",
              title: `Nivel ${currentLevel}`,
              message: `Has subido desde el nivel ${previousLevel}.`,
              actionLabel: "Ver Mi XP",
              actionTo: "/mi-xp",
              durationMs: 8000,
            });
          }
        }
      } catch {
        // Este comprobador es decorativo: nunca debe bloquear una acción principal.
      } finally {
        inFlightRef.current = false;
      }
    },
    [userKey]
  );

  const scheduleCheck = useCallback(
    (reason = "scheduled") => {
      if (!userKey) return;
      if (timerRef.current) window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => runCheck(reason), CHECK_DELAY_MS);
    },
    [runCheck, userKey]
  );

  useEffect(() => {
    if (!userKey) return undefined;
    scheduleCheck("auth-ready");
    const onRefresh = (event) => {
      scheduleCheck(event?.detail?.reason || "progress-refresh");
    };
    const onFocus = () => {
      if (document.visibilityState === "visible") scheduleCheck("visibility");
    };

    window.addEventListener("xphub:progress-refresh-request", onRefresh);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
      window.removeEventListener("xphub:progress-refresh-request", onRefresh);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [scheduleCheck, userKey]);

  return null;
}
